import { CircleHelp } from "lucide-react";
import type { ReactNode } from "react";

import type { RentVsBuyRender } from "../../../lib/agent-types";
import { formatCurrency, formatCurrencyFromCents } from "../../../lib/financials";
import { WidgetFrame } from "../widget-frame";
import { Tooltip } from "../../ui/tooltip";

export function RentVsBuyWidget({ payload }: { payload: RentVsBuyRender }) {
  const comparison = payload.comparison;

  if (comparison) {
    const horizonDelta =
      comparison.buy.totalOwnershipCost - comparison.rent.totalRentPaid;
    const horizonDeltaAmount = Math.abs(horizonDelta);
    const annualizedHorizonDelta = annualizeByYears(
      horizonDeltaAmount,
      comparison.rent.yearsInHome,
    );
    const leaningTone = getRentVsBuyLeaningTone(horizonDelta);
    const deltaLabel =
      horizonDelta > 0
        ? "Buying costs more"
        : horizonDelta < 0
          ? "Buying saves"
          : "Even over horizon";
    const monthlyDeltaCopy = getMonthlyDeltaCopy(comparison.monthlyDelta);

    return (
      <WidgetFrame
        data-widget-type="rent_vs_buy"
        footer={
          <div className={getSummaryPanelClassName(leaningTone)}>
            <p>{comparison.summary.recommendedAction}</p>
            {monthlyDeltaCopy ? (
              <p className="mt-1 text-xs text-current/80">{monthlyDeltaCopy}</p>
            ) : null}
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <ScenarioCard
            label="Buy"
            sublabel="monthly (PITI)"
            value={formatCurrencyFromCents(comparison.buy.monthlyPITI)}
          />
          <ScenarioCard
            label="Rent"
            sublabel={`${comparison.rent.yearsInHome}-yr horizon`}
            value={formatCurrencyFromCents(comparison.rent.startingMonthlyRent)}
          />
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <MetricCard
            label={deltaLabel}
            labelAccessory={<AnnualizedDifferenceTooltip />}
            value={`${formatCurrencyFromCents(annualizedHorizonDelta)}/yr`}
            detail={`${formatCurrencyFromCents(horizonDeltaAmount)} net over ${comparison.rent.yearsInHome} years`}
          />
          <MetricCard
            label="Break-even"
            labelAccessory={<BreakEvenTooltip />}
            value={
              comparison.breakevenMonths
                ? `~${comparison.breakevenMonths} mo`
                : "Outside horizon"
            }
            detail={
              comparison.breakevenMonths
                ? "Buying catches up inside your timeline."
                : "Buying does not catch up inside your timeline."
            }
          />
        </div>
      </WidgetFrame>
    );
  }

  return <LegacyFallback payload={payload} />;
}

function ScenarioCard({
  label,
  sublabel,
  value,
}: {
  label: string;
  sublabel: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl bg-background px-3 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 text-lg font-bold tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{sublabel}</p>
    </div>
  );
}

function MetricCard({
  detail,
  label,
  labelAccessory,
  value,
}: {
  detail?: string;
  label: string;
  labelAccessory?: ReactNode;
  value: string;
}) {
  return (
    <div className="rounded-2xl bg-background px-3 py-2.5">
      <div className="flex items-center gap-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </p>
        {labelAccessory}
      </div>
      <p className="mt-2 text-base font-semibold tabular-nums text-foreground">
        {value}
      </p>
      {detail ? (
        <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
      ) : null}
    </div>
  );
}

function getRentVsBuyLeaningTone(horizonDelta: number) {
  return horizonDelta <= 0 ? "success" : "warning";
}

function getSummaryPanelClassName(tone: "success" | "warning") {
  return tone === "success"
    ? "rounded-2xl border border-success/20 bg-success/8 px-3 py-2 text-sm leading-6 text-success-foreground"
    : "rounded-2xl border border-warning/25 bg-warning/10 px-3 py-2 text-sm leading-6 text-warning-foreground";
}

function getMonthlyDeltaCopy(monthlyDelta: number) {
  const amount = Math.abs(monthlyDelta);

  if (amount === 0) {
    return null;
  }

  return monthlyDelta > 0
    ? `Today, buying is ${formatCurrencyFromCents(amount)}/mo more than renting.`
    : `Today, buying is ${formatCurrencyFromCents(amount)}/mo less than renting.`;
}

function annualizeByYears(amount: number, years: number) {
  if (years <= 1) {
    return amount;
  }

  return Math.round(amount / years);
}

function AnnualizedDifferenceTooltip() {
  return (
    <Tooltip
      content={
        <div className="max-w-72 leading-5">
          This is an annualized net difference, not just today&apos;s monthly
          payment gap times 12. Sill compares total rent paid with total
          ownership cost after principal paydown, expected appreciation, and
          estimated sale proceeds, then averages that difference across your
          selected stay.
        </div>
      }
    >
      <button
        type="button"
        aria-label="About annualized rent vs. buy difference"
        className="rounded-full text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

function BreakEvenTooltip() {
  return (
    <Tooltip
      content={
        <div className="max-w-72 leading-5">
          Break-even is the point when cumulative net ownership cost catches up
          to cumulative rent paid. If it says outside horizon, buying never
          catches up within the stay length you selected.
        </div>
      }
    >
      <button
        type="button"
        aria-label="About rent vs. buy break-even"
        className="rounded-full text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

function LegacyFallback({ payload }: { payload: RentVsBuyRender }) {
  const buyTotal = payload.buy.results[0]?.raw.payment_at_target_price?.total;

  return (
    <WidgetFrame data-widget-type="rent_vs_buy">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-background px-3 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Buy
          </p>
          <p className="mt-2 text-lg font-bold tabular-nums text-foreground">
            {formatCurrencyFromCents(buyTotal)}
          </p>
          <p className="text-xs text-muted-foreground">monthly (PITI)</p>
        </div>
        <div className="rounded-2xl bg-background px-3 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Rent
          </p>
          <p className="mt-2 text-lg font-bold tabular-nums text-foreground">
            {formatCurrency(payload.rent.monthlyRent)}
          </p>
          <p className="text-xs text-muted-foreground">
            monthly
            {typeof payload.rent.yearsInHome === "number"
              ? ` · ${payload.rent.yearsInHome}-yr horizon`
              : ""}
          </p>
        </div>
      </div>
    </WidgetFrame>
  );
}
